/* ═══════════════════════════════════════
   AirLog File Helpers
   Functions for file type detection,
   filename parsing, and truncation
   ═══════════════════════════════════════ */

   import { FILE_TYPE_MAP, LANGUAGE_MAP, LANGUAGE_LIST } from './constants'

   export const getFileTypeInfo = (filename, mimeType) => {
     if (!filename) return { label: 'File', letter: 'F', bg: '#8E8E93' }
     const ext = filename.split('.').pop()?.toLowerCase()
     return FILE_TYPE_MAP[ext] || {
       label: ext?.toUpperCase() || 'File',
       letter: (ext?.[0] || 'F').toUpperCase(),
       bg: '#8E8E93'
     }
   }
   
   export const getFilenameFromUrl = (url) => {
     if (!url) return 'File'
     try {
       const parts = decodeURIComponent(url).split('/')
       const last = parts[parts.length - 1]
       const clean = last.replace(/^[^/]+?-\d+-[\d.]+?\./, '')
       return clean || last
     } catch {
       return 'File'
     }
   }
   
   export const truncateFilename = (name, maxLen = 32) => {
     if (!name || name.length <= maxLen) return name
     const ext = name.includes('.') ? '.' + name.split('.').pop() : ''
     const base = name.slice(0, name.length - ext.length)
     if (base.length <= maxLen) return name
     const keepStart = Math.ceil((maxLen - 3) * 0.6)
     const keepEnd = Math.floor((maxLen - 3) * 0.4)
     return base.slice(0, keepStart) + '...' + base.slice(-keepEnd) + ext
   }
   
   export const isDocumentLog = (log) => {
     const url = log.media_url || log.media_urls?.[0]
     if (!url) return false
     if (!log.media_type) return true
     return (
       !log.media_type.startsWith('image/') &&
       !log.media_type.startsWith('video/') &&
       !log.media_type.startsWith('audio/')
     )
   }
   
   /* ───────────────────────────────────────────────────────────────
    *  Code-log language helpers
    * ─────────────────────────────────────────────────────────────── */
   
   /* Get language metadata by id (with safe fallback). */
   export const getLanguageInfo = (langId) => {
     if (!langId) return null
     return LANGUAGE_MAP[langId] || null
   }
   
   /* Map a filename extension to a language id (or null). */
   export const detectLanguageFromFilename = (filename) => {
     if (!filename) return null
     const ext = filename.split('.').pop()?.toLowerCase()
     if (!ext) return null
     for (const id of LANGUAGE_LIST) {
       const info = LANGUAGE_MAP[id]
       if (info.aliases?.includes(ext)) return id
     }
     return null
   }
   
   /* ── Auto-detect language from pasted code content ──
    *
    *  Tiny heuristic — not perfect, just "feels magical" when right.
    *  Scans for strong signal phrases that almost-uniquely identify
    *  a language. Returns null if nothing matches confidently.
    *  Order matters: we check the most-specific patterns first.
    */
   export const detectLanguageFromContent = (code) => {
     if (!code || code.length < 4) return null
     const sample = code.slice(0, 2000)  // only scan the first ~2KB
   
     // HTML / Markup
     if (/<!DOCTYPE\s+html|<html[\s>]|<\/(div|p|span|body|head)>/i.test(sample)) return 'html'
   
     // Swift
     if (/\bimport\s+(SwiftUI|UIKit|Foundation)\b/.test(sample)) return 'swift'
     if (/\bfunc\s+\w+\s*\(.*\)\s*->/.test(sample) && /\bvar\s+\w+\s*:\s*\w+/.test(sample)) return 'swift'
   
     // Python
     if (/^\s*def\s+\w+\s*\(/m.test(sample)) return 'python'
     if (/^\s*from\s+[\w.]+\s+import\s+/m.test(sample)) return 'python'
     if (/^\s*import\s+\w+\s*$/m.test(sample) && /:\s*$/m.test(sample)) return 'python'
   
     // PHP
     if (/^\s*<\?php\b/.test(sample)) return 'php'
   
     // C / C++ / C#
     if (/^\s*#include\s*[<"]/m.test(sample)) {
       if (/\bstd::|\bclass\s+\w+|template\s*</.test(sample)) return 'cpp'
       return 'c'
     }
     if (/\busing\s+System;|\bnamespace\s+\w+|\bpublic\s+class\s+\w+/.test(sample)) return 'csharp'
   
     // Rust
     if (/\bfn\s+\w+\s*\(/.test(sample) && /\blet\s+(mut\s+)?\w+/.test(sample)) return 'rust'
   
     // Go
     if (/^\s*package\s+\w+\s*$/m.test(sample) && /\bfunc\s+\w+\s*\(/.test(sample)) return 'go'
   
     // Java / Kotlin (Java first because more common)
     if (/\bpublic\s+(static\s+)?(void|class)\s+\w+/.test(sample)) return 'java'
     if (/\bfun\s+\w+\s*\(/.test(sample) && /\bval\s+\w+/.test(sample)) return 'kotlin'
   
     // Ruby
     if (/^\s*def\s+\w+/m.test(sample) && /\bend\s*$/m.test(sample)) return 'ruby'
   
     // SQL
     if (/^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE\s+TABLE)\b/im.test(sample)) return 'sql'
   
     // Bash
     if (/^#!\/(bin\/)?(bash|sh|zsh)/.test(sample)) return 'bash'
     if (/^\s*\$\s+\w/m.test(sample)) return 'bash'
   
     // CSS
     if (/^\s*[.#]?[\w-]+\s*\{[^}]*[\w-]+\s*:/m.test(sample) && !/=>|\bfunction\b/.test(sample)) return 'css'
   
     // JSON
     if (/^\s*[{[]/.test(sample) && /"[\w-]+"\s*:/.test(sample) && !/\bfunction\b|=>|\bconst\b/.test(sample)) return 'json'
   
     // TypeScript (interfaces, type annotations)
     if (/\binterface\s+\w+\s*\{/.test(sample) || /:\s*(string|number|boolean|any)\s*[;,=)]/.test(sample)) return 'typescript'
   
     // JavaScript (catch-all for JS-shaped code)
     if (/\b(const|let|var)\s+\w+\s*=/.test(sample) && /=>|\bfunction\b/.test(sample)) return 'javascript'
     if (/\brequire\s*\(\s*['"]/.test(sample)) return 'javascript'
   
     // Markdown
     if (/^#{1,6}\s+\S/m.test(sample) || /^\s*[-*]\s+\S/m.test(sample)) return 'markdown'
   
     return null
   }
   
   /* Used by InputBar paste handler — combines filename + content
    * detection. Tries filename first (most reliable), then content. */
   export const detectLanguageFromPaste = ({ filename, content }) => {
     const fromName = detectLanguageFromFilename(filename)
     if (fromName) return fromName
     return detectLanguageFromContent(content)
   }